import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import ConsentContext from './ConsentContext';
import { ConsentContextType, ConsentState } from '../model';
import { defaultConsent, loadConsent, saveConsent } from '../functions/consent';

type GTag = (
  command: string,
  action: string,
  params?: Record<string, unknown>
) => void;

interface ConsentProviderProps {
  children: ReactNode;
  gaId: string;
  clarityId?: string;
  storageKey?: string;
}

const updateConsentModeFromState = (state: ConsentState) => {
  try {
    const analytics = state.categories.analytics ? 'granted' : 'denied';
    const adStorage = 'denied';
    if (typeof window.gtag === 'function') {
      (window.gtag as GTag)('consent', 'update', {
        analytics_storage: analytics,
        ad_storage: adStorage,
        ad_user_data: adStorage,
        ad_personalization: adStorage,
      });
    }
  } catch {
    // ignore
  }
};

const injectClarity = (clarityId?: string) => {
  if (!clarityId) return;
  if ('clarity' in window) return;
  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = true;
  s.innerHTML = `
  (function(c,l,a,r,i,t,y){
    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
  })(window, document, "clarity", "script", "${clarityId}");
  `;
  document.head.appendChild(s);
};

export const ConsentProvider: FC<ConsentProviderProps> = ({
  children,
  gaId,
  clarityId,
  storageKey,
}) => {
  const [consent, setConsent] = useState<ConsentState>(defaultConsent);
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    const saved = loadConsent();
    setConsent(saved);
    updateConsentModeFromState(saved);
  }, [storageKey]);

  useEffect(() => {
    if (!consent.given) return;
    saveConsent(consent, gaId);
    updateConsentModeFromState(consent);

    const allowAnalytics = consent.categories.analytics;

    (async () => {
      if (allowAnalytics && gaId) {
        if (typeof window.gtag === 'function') {
          (window.gtag as GTag)('consent', 'update', {
            analytics_storage: 'granted',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
          });
        }
      }

      if (allowAnalytics && clarityId) {
        injectClarity(clarityId);
      }
    })();
  }, [consent, gaId, clarityId, storageKey]);

  const updateCategories = (
    partial: Partial<ConsentState['categories']>,
    markGiven = true
  ) => {
    setConsent((prev: ConsentState) => {
      const isAnalyticsEnabled = prev.categories.analytics;
      const willEnableAnalytics = partial.analytics;

      if (markGiven && (isAnalyticsEnabled || willEnableAnalytics)) {
        const pushCookiesEvents = () => {
          // placeholder: consumers can hook into analytics callbacks
          // or library can expose events in later iteration
        };

        // call pushCookiesEvents after gtag available
        Promise.resolve().then(() => pushCookiesEvents());
      }

      return {
        given: markGiven ? true : prev.given,
        categories: { ...prev.categories, ...partial, necessary: true },
      };
    });
  };

  const value = useMemo<ConsentContextType>(
    () => ({
      consent,
      setConsent,
      updateCategories,
      openPrefs: () => setPrefsOpen(true),
      closePrefs: () => setPrefsOpen(false),
      prefsOpen,
    }),
    [consent, prefsOpen]
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
};

export default ConsentProvider;
